// Client/src/components/Sidebar/RSAAnalysis/PathsPanels/RoutePointPicker.jsx
// One road-selection unit for the LRS panel: search input + results list when
// no route is selected; a chip + valid-range line when one is. Purely
// presentational — the parent owns selection/geometry state.
import React, { useState, useEffect, useRef } from 'react';
import { formatRanges } from '../../../../utils/stateLrs/lrsMath';
import { describeDirectionLabel } from '../../../../utils/stateLrs/aldot';

const MAX_RESULTS = 50;
const LOCAL_SEARCH_DEBOUNCE_MS = 300;

export default function RoutePointPicker({
  adapter,
  statewide,            // RouteEntry[] | null (shared, loaded by the panel)
  includeLocal,         // bool (shared toggle owned by the panel)
  selectedRoute,        // RouteEntry | null
  geometry,             // { sections, mRanges } | null
  geometryError,        // string | null
  onSelectRoute,        // (route) => void
  onClearRoute,         // () => void
  onRetryGeometry,      // () => void — re-fetch this route's geometry after an error
  searchPlaceholder,
  showRange = true,     // END hides its duplicate range line while mirrored
  mirroredNote = null,  // e.g. '(same as start road)' on an untouched END chip
}) {
  const [query, setQuery] = useState('');
  const [localResults, setLocalResults] = useState([]);
  const localSearchTimer = useRef(null);
  // True once a route has been selected in this picker — used to autofocus the
  // search input when the user clicks "Change road" (but not on initial open).
  const hadSelectionRef = useRef(false);
  useEffect(() => {
    if (selectedRoute) hadSelectionRef.current = true;
  }, [selectedRoute]);

  const measureWord = selectedRoute?.network === 'milepoint' ? 'milepoint' : 'milepost';

  // Debounced live search for local roads (per picker — each has its own query).
  useEffect(() => {
    if (!includeLocal || !query.trim()) {
      setLocalResults([]);
      return undefined;
    }
    if (localSearchTimer.current) clearTimeout(localSearchTimer.current);
    localSearchTimer.current = setTimeout(() => {
      adapter.searchLocalRoutes(query)
        .then(setLocalResults)
        .catch(() => setLocalResults([]));
    }, LOCAL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(localSearchTimer.current);
  }, [includeLocal, query, adapter]);

  if (selectedRoute) {
    return (
      <div className="lrs-point-picker">
        <div className="lrs-route-panel-selected">
          <div className="lrs-route-panel-selected-info">
            <strong>{selectedRoute.label}</strong>
            {mirroredNote && <span className="lrs-point-picker-note">{mirroredNote}</span>}
          </div>
          <button
            type="button"
            className="lrs-point-picker-change"
            onClick={onClearRoute}
            title="Pick a different road"
          >
            <i className="fas fa-pen" aria-hidden /> Change road
          </button>
        </div>
        {geometryError && (
          <div className="lrs-route-panel-error" role="alert">
            <span>{geometryError} You can retry, or place this route by clicking the map.</span>
            {onRetryGeometry && (
              <button type="button" onClick={onRetryGeometry}>Retry</button>
            )}
          </div>
        )}
        {!geometry && !geometryError && (
          <div className="lrs-route-panel-notice">Loading route geometry…</div>
        )}
        {showRange && geometry && (
          <div className="lrs-route-panel-range">
            Valid {measureWord} range: {formatRanges(geometry.mRanges)}
          </div>
        )}
        {describeDirectionLabel(selectedRoute.label) && (
          <div className="lrs-point-picker-dirnote">
            <i className="fas fa-circle-info" aria-hidden />{' '}
            {selectedRoute.label.includes('Non-Inventory') ? 'Non-Inventory' : 'Inventory'}
            {' — '}{describeDirectionLabel(selectedRoute.label)}
          </div>
        )}
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const statewideMatches = (statewide ?? [])
    .filter((r) => !needle || r.label.toLowerCase().includes(needle)) // contains, not prefix
    .slice(0, MAX_RESULTS);
  const results = [...statewideMatches, ...localResults].slice(0, MAX_RESULTS);

  return (
    <div className="lrs-point-picker">
      <input
        type="text"
        className="lrs-route-panel-search"
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={hadSelectionRef.current}
      />
      {query.trim() && (
        <ul className="lrs-route-panel-results">
          {results.map((r) => (
            <li key={r.key}>
              <button
                type="button"
                onClick={() => { onSelectRoute(r); setQuery(''); }}
              >
                <span className="lrs-route-panel-result-label">{r.label}</span>
                {describeDirectionLabel(r.label) && (
                  <i
                    className="fa-solid fa-circle-info lrs-route-panel-result-info"
                    title={describeDirectionLabel(r.label)}
                    aria-label={describeDirectionLabel(r.label)}
                    role="img"
                  />
                )}
              </button>
            </li>
          ))}
          {results.length === 0 && statewide && (
            <li className="lrs-route-panel-noresults">
              No matches{includeLocal ? ' — refine your search' : ''}
            </li>
          )}
          {results.length >= MAX_RESULTS && (
            <li className="lrs-route-panel-noresults">
              Showing first {MAX_RESULTS} — refine your search
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
